import React from 'react';
import { resolveImage } from '../../utils/api';

/* ── Shared keyframes ── */
const KEYFRAMES = `
  @keyframes floatBadge  { 0%,100%{transform:translateY(0px)} 50%{transform:translateY(-8px)} }
  @keyframes sparkleIn   { 0%,100%{opacity:0;transform:scale(0)} 50%{opacity:1;transform:scale(1)} }
  @keyframes shimmerBar  { 0%{transform:scaleX(0)} 100%{transform:scaleX(1)} }
  @keyframes pulseRing   { 0%{box-shadow:0 0 0 0 rgba(123,29,58,.4)} 70%{box-shadow:0 0 0 18px rgba(123,29,58,0)} 100%{box-shadow:0 0 0 0 rgba(123,29,58,0)} }
  @keyframes fadeSlideUp { 0%{opacity:0;transform:translateY(20px)} 100%{opacity:1;transform:translateY(0)} }
  @keyframes fadeSlideDown { 0%{opacity:0;transform:translateY(-16px)} 100%{opacity:1;transform:translateY(0)} }
`;

/* Helper: read { en, zh } or plain string */
const t = (field, lang = 'en') => {
  if (!field) return '';
  if (typeof field === 'string') return field;
  return field[lang] || field.en || '';
};

/* ─── Desktop right-panel: photo with floating overlays ─── */
function DesktopDancer({ content, lang = 'en' }) {
  const imageUrl = content?.heroImageUrl
    ? resolveImage(content.heroImageUrl)
    : 'https://images.pexels.com/photos/8935911/pexels-photo-8935911.jpeg?auto=compress&cs=tinysrgb&w=1200';
  const label      = t(content?.heroLabel, lang)   || t({ en: 'Classical Ballet', zh: '古典芭蕾' }, lang);
  const badge1Parts = (t(content?.heroBadge1, lang) || t({ en: '10+\nYears of Dance', zh: '10+\n舞蹈年限' }, lang)).replace(/\\n/g, '\n').split('\n');
  const badge2Parts = (t(content?.heroBadge2, lang) || t({ en: '4.9 / 5.0\nStudent Rating', zh: '4.9 / 5.0\n學生評價' }, lang)).replace(/\\n/g, '\n').split('\n');

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: 640, overflow: 'hidden' }}>
      <style>{KEYFRAMES}</style>

      <img src={imageUrl} alt="Hero Dancer" style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        objectFit: 'cover', objectPosition: 'center top',
        filter: 'brightness(0.92) contrast(1.05) saturate(0.88)',
      }} />

      {/* Bottom gradient */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(253,246,248,0.08) 0%, rgba(248,232,240,0.12) 40%, rgba(240,210,225,0.45) 80%, rgba(248,232,240,0.88) 100%)', pointerEvents: 'none' }} />
      {/* Left edge fade */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(248,232,240,0.6) 0%, transparent 18%)', pointerEvents: 'none' }} />

      {/* Floating badge top-right */}
      <div style={{ position: 'absolute', top: 32, right: 28, background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)', border: '1px solid rgba(123,29,58,0.15)', padding: '12px 16px', borderRadius: 2, animation: 'floatBadge 3.5s ease-in-out infinite', boxShadow: '0 4px 20px rgba(123,29,58,0.12)' }}>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700, color: '#7b1d3a', lineHeight: 1 }}>{badge1Parts[0]}</div>
        {badge1Parts[1] && <div style={{ fontSize: 9, color: '#9a9a9a', letterSpacing: 1.5, textTransform: 'uppercase', marginTop: 3 }}>{badge1Parts[1]}</div>}
      </div>

      {/* Rating badge left */}
      <div style={{ position: 'absolute', top: '40%', left: 20, background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)', border: '1px solid rgba(123,29,58,0.12)', padding: '10px 14px', borderRadius: 2, animation: 'floatBadge 4s ease-in-out infinite .8s', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', gap: 2, marginBottom: 4 }}>
          {[1,2,3,4,5].map(i => <div key={i} style={{ width: 10, height: 10, background: '#c04060', clipPath: 'polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%)' }} />)}
        </div>
        <div style={{ fontSize: 11, color: '#4a4a4a', fontWeight: 600 }}>{badge2Parts[0]}</div>
        {badge2Parts[1] && <div style={{ fontSize: 10, color: '#9a9a9a', letterSpacing: .5 }}>{badge2Parts[1]}</div>}
      </div>

      {/* Bottom class card */}
      <div style={{ position: 'absolute', bottom: 28, left: 20, right: 20, background: 'rgba(255,255,255,0.94)', backdropFilter: 'blur(10px)', border: '1px solid rgba(123,29,58,0.1)', padding: '16px 18px', borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.1)', animation: 'fadeSlideUp .8s ease both 0.3s' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 15, fontWeight: 700, color: '#111', marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 11, color: '#9a9a9a', letterSpacing: 1, textTransform: 'uppercase' }}>{t({en:'Beginner · Intermediate · Advanced', zh:'初級 · 中級 · 高級'}, lang)}</div>
          </div>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#7b1d3a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, animation: 'pulseRing 2.5s ease-in-out infinite' }}>🩰</div>
        </div>
        <div style={{ height: 3, background: '#f0e0e8', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: '78%', background: 'linear-gradient(to right,#7b1d3a,#c04060)', borderRadius: 2, animation: 'shimmerBar .9s ease both .5s', transformOrigin: 'left' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
          <span style={{ fontSize: 10, color: '#9a9a9a' }}>{t({en:'Batch capacity', zh:'班級滿員率'}, lang)}</span>
          <span style={{ fontSize: 10, color: '#7b1d3a', fontWeight: 600 }}>{t({en:'78% filled', zh:'已滿 78%'}, lang)}</span>
        </div>
      </div>

      {/* Sparkles */}
      {[{ top:'12%',right:'15%',size:6,delay:'0s'},{ top:'25%',left:'12%',size:4,delay:'.7s'},{ top:'55%',right:'10%',size:5,delay:'1.4s'},{ top:'70%',left:'25%',size:3,delay:'.3s'}].map((s,i) => (
        <div key={i} style={{ position:'absolute',...s, width:s.size, height:s.size, borderRadius:'50%', background:'#c04060', opacity:0, animation:'sparkleIn 3s ease-in-out infinite', animationDelay:s.delay }} />
      ))}

      {/* Label pill */}
      <div style={{ position: 'absolute', top: 32, left: 24, display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(123,29,58,0.85)', padding: '6px 12px' }}>
        <span style={{ fontSize: 13 }}>🩰</span>
        <span style={{ fontFamily: "'Playfair Display',serif", fontStyle: 'italic', fontSize: 12, color: 'rgba(255,255,255,.9)', letterSpacing: 1 }}>{label}</span>
      </div>
    </div>
  );
}

/* ─── MOBILE: full-bleed background with text overlay ─── */
function MobileHero({ content, heroHeadlineParts, scrollTo, lang = 'en' }) {
  const imageUrl = content?.heroImageUrl
    ? resolveImage(content.heroImageUrl)
    : 'https://images.pexels.com/photos/8935911/pexels-photo-8935911.jpeg?auto=compress&cs=tinysrgb&w=1200';
  const label      = t(content?.heroLabel, lang)   || 'Classical Ballet';
  const badge1Parts = (t(content?.heroBadge1, lang) || '10+\nYears of Dance').replace(/\\n/g, '\n').split('\n');

  return (
    <div className="lp-mobile-hero">
      <style>{KEYFRAMES}</style>

      {/* Full-bleed background image */}
      <img src={imageUrl} alt="Hero Dancer" className="lp-mobile-hero-img" />

      {/* Dark gradient overlay so text is readable */}
      <div className="lp-mobile-hero-overlay" />

      {/* Content sits on top */}
      <div className="lp-mobile-hero-body">

        {/* Label pill */}
        <div className="lp-mobile-label-pill" style={{ animation: 'fadeSlideDown .6s ease both 0.1s' }}>
          <span>🩰</span>
          <span>{label}</span>
        </div>

        {/* Headline */}
        <h1 className="lp-mobile-h1" style={{ animation: 'fadeSlideUp .7s ease both 0.2s' }}>
          {heroHeadlineParts[0]}
          {heroHeadlineParts[1] && (
            <><br /><em className="lp-mobile-h1-em">{heroHeadlineParts[1]}</em></>
          )}
          {heroHeadlineParts[2] && (
            <><br /><span className="lp-mobile-h1-light">{heroHeadlineParts[2]}</span></>
          )}
        </h1>

        {/* Sub text */}
        <p className="lp-mobile-sub" style={{ animation: 'fadeSlideUp .7s ease both 0.35s' }}>
          {t(content?.heroSubtext, lang) || 'ETS brings the full dance academy experience — from Ballet to Hip Hop. Track progress, connect with world-class tutors.'}
        </p>

        {/* Buttons */}
        <div className="lp-mobile-btns" style={{ animation: 'fadeSlideUp .7s ease both 0.5s' }}>
          <button className="btn btn-primary btn-lg lp-mobile-btn-primary" onClick={() => scrollTo('contact')}>
            {t({ en: 'Start Dancing Free →', zh: '開始免費體驗 →' }, lang)}
          </button>
          <button className="btn lp-mobile-btn-ghost" onClick={() => scrollTo('classes')}>
            {t({ en: 'Explore Classes', zh: '探索課程' }, lang)}
          </button>
        </div>

        {/* Mini stats strip */}
        <div className="lp-mobile-stats-strip" style={{ animation: 'fadeSlideUp .7s ease both 0.65s' }}>
          <div className="lp-mobile-stat">
            <span className="lp-mobile-stat-num">{badge1Parts[0]}</span>
            <span className="lp-mobile-stat-lbl">{badge1Parts[1] || t({ en: 'Years of Dance', zh: '舞蹈年限' }, lang)}</span>
          </div>
          <div className="lp-mobile-stat-sep" />
          <div className="lp-mobile-stat">
            <span className="lp-mobile-stat-num">12K+</span>
            <span className="lp-mobile-stat-lbl">{t({ en: 'Active Dancers', zh: '活躍舞者' }, lang)}</span>
          </div>
          <div className="lp-mobile-stat-sep" />
          <div className="lp-mobile-stat">
            <span className="lp-mobile-stat-num">4.9★</span>
            <span className="lp-mobile-stat-lbl">{t({ en: 'Rating', zh: '評分' }, lang)}</span>
          </div>
        </div>

        {/* Floating badge top-right */}
        <div className="lp-mobile-float-badge" style={{ animation: 'floatBadge 3.5s ease-in-out infinite' }}>
          <div className="lp-mobile-float-num">{badge1Parts[0]}</div>
          <div className="lp-mobile-float-lbl">{badge1Parts[1] || t({ en: 'Years of Dance', zh: '舞蹈年限' }, lang)}</div>
        </div>
      </div>

      {/* Scroll hint */}
      <div className="lp-mobile-scroll-hint">
        <div className="lp-mobile-scroll-dot" />
      </div>
    </div>
  );
}

/* ─── Main Hero export ─── */
export default function Hero({ content, scrollTo, lang = 'en' }) {
  const rawHeadline = t(content?.heroHeadline, lang) || 'Where\nBallet\nBecomes Life.';
  const heroHeadlineParts = rawHeadline.replace(/\\n/g, '\n').split('\n');

  return (
    <>
      {/* ── DESKTOP layout (shown ≥769px) ── */}
      <section id="hero" className="lp-hero-section">
        {/* Left: Text */}
        <div className="lp-hero-left">
          <div style={{ position: 'absolute', left: 0, top: '15%', bottom: '15%', width: 3, background: 'linear-gradient(to bottom, transparent, #c04060, transparent)' }} />
          <div className="lp-hero-content">
            <div className="lp-hero-eyebrow reveal" style={{ color: '#c04060' }}>{t({ en: 'Every Talent Shines Academy', zh: 'Every Talent Shines 舞蹈學院' }, lang)}</div>
            <h1 className="lp-hero-h1 reveal" style={{ color: '#111111', fontSize: 'clamp(52px,6.5vw,88px)', lineHeight: 0.92, marginBottom: 28 }}>
              {heroHeadlineParts[0]}
              {heroHeadlineParts[1] && <><br /><em style={{ color: '#7b1d3a', fontStyle: 'italic' }}>{heroHeadlineParts[1]}</em></>}
              {heroHeadlineParts[2] && <><br /><span style={{ fontWeight: 400, opacity: .55, fontSize: '.68em' }}>{heroHeadlineParts[2]}</span></>}
            </h1>
            <p className="lp-hero-sub reveal" style={{ color: '#4a4a4a', maxWidth: 460 }}>
              {t(content?.heroSubtext, lang) || 'ETS brings the full dance academy experience to your fingertips — from Ballet to Hip Hop, Salsa to Kathak.'}
            </p>
            <div className="lp-hero-btns reveal">
              <button className="btn btn-primary btn-lg" onClick={() => scrollTo('contact')}>{t({ en: 'Start Dancing Free →', zh: '開始免費體驗 →' }, lang)}</button>
              <button className="btn btn-outline btn-lg" onClick={() => scrollTo('classes')}>{t({ en: 'Explore Classes', zh: '探索課程' }, lang)}</button>
            </div>
            <div className="lp-hero-mini-stats reveal">
              <div><div className="lp-hero-mini-num">12K+</div><div className="lp-hero-mini-lbl">{t({ en: 'Active Dancers', zh: '活躍舞者' }, lang)}</div></div>
              <div className="lp-hero-divider" />
              <div><div className="lp-hero-mini-num">150+</div><div className="lp-hero-mini-lbl">{t({ en: 'Partner Academies', zh: '合作學院' }, lang)}</div></div>
              <div className="lp-hero-divider" />
              <div><div className="lp-hero-mini-num">45+</div><div className="lp-hero-mini-lbl">{t({ en: 'Expert Tutors', zh: '專業導師' }, lang)}</div></div>
            </div>
          </div>
        </div>

        {/* Right: Photo */}
        <div className="lp-hero-right">
          <DesktopDancer content={content} lang={lang} />
          <div className="lp-scroll-indicator" style={{ bottom: 24, zIndex: 10 }}>
            <span>{t({ en: 'Scroll', zh: '向下滑動' }, lang)}</span>
            <div className="lp-scroll-line" style={{ background: 'rgba(123,29,58,.4)' }} />
          </div>
        </div>
      </section>

      {/* ── MOBILE full-screen hero (shown ≤768px) ── */}
      <MobileHero content={content} heroHeadlineParts={heroHeadlineParts} scrollTo={scrollTo} lang={lang} />
    </>
  );
}
